import { useState, useEffect } from "react";
import { apiClient } from "@/lib/api";
import { User } from "@/types/user";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronLeft, ChevronRight, Search, Activity } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/use-debounce";

export default function UsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Initialize from URL params or default to 1/10
  const page = Number(searchParams.get("page")) || 1;
  const limit = Number(searchParams.get("limit")) || 10;
  const initialSearch = searchParams.get("search") || "";

  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const debouncedSearch = useDebounce(searchTerm, 500);

  // Helper to update URL params
  const updateParams = (newPage: number, newLimit: number, search: string) => {
    const params: Record<string, string> = {
        page: String(newPage),
        limit: String(newLimit)
    };
    if (search) params.search = search;
    setSearchParams(params);
  };

  // Sync search state with URL when debounced value changes
  useEffect(() => {
    if (debouncedSearch !== initialSearch) {
        updateParams(1, limit, debouncedSearch); // Reset to page 1 on search
    }
  }, [debouncedSearch]);

  // Use React Query for caching and pagination
  const { data, isLoading, isError } = useQuery({
    queryKey: ["adminUsers", page, limit, debouncedSearch],
    queryFn: () => apiClient.getAdminUsers(page, limit, debouncedSearch),
    placeholderData: keepPreviousData, // Keep showing previous page data while loading new page
    staleTime: 5 * 60 * 1000, // Cache data for 5 minutes
  });

  const users = data ? (Array.isArray(data) ? data : data.items) : [];
  const total = data && !Array.isArray(data) ? data.total : users.length;
  const totalPages = Math.ceil(total / limit);

  if (isLoading && !data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return <div className="text-center py-10 text-red-500">Failed to load users</div>;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] font-ibm overflow-hidden">
      {/* Header & Filters Container */}
      <div className="flex-none space-y-6 pb-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
             <div>
              <h1 className="text-3xl font-semibold text-foreground tracking-tight font-cabinet">
                Users
              </h1>
              <p className="text-muted-foreground font-ibm text-sm mt-1 max-w-sm">
                Manage platform users and roles.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
               <Button
                 onClick={() => navigate('/admin/monitoring')}
                 variant="outline"
                 size="sm"
                 className="h-9 px-4 font-ibm bg-card/60 backdrop-blur-sm border-border hover:bg-primary/10 transition-colors"
               >
                 <Activity className="w-4 h-4 mr-2" />
                 System Monitoring
               </Button>
               <Badge variant="outline" className="text-muted-foreground h-9 px-4 flex items-center justify-center font-ibm bg-card/60 backdrop-blur-sm">
                  Total: {total}
              </Badge>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                  <Search className="w-4 h-4" />
                  </div>
                  <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 bg-card/60 backdrop-blur-sm border border-border rounded-lg text-sm placeholder:text-muted-foreground text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-sm font-ibm h-[38px]"
                  />
              </div>
        </div>
      </div>

      {/* Scrollable Table Container */}
      <div className="flex-1 min-h-0 rounded-xl border border-border bg-card shadow-sm overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto custom-scrollbar">
          <Table className="min-w-[1000px]">
            <TableHeader className="sticky top-0 bg-card z-10 shadow-sm border-b border-border">
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="font-cabinet font-semibold text-foreground w-[150px]">Name</TableHead>
                <TableHead className="font-cabinet font-semibold text-foreground w-[200px]">Email</TableHead>
                <TableHead className="font-cabinet font-semibold text-foreground">Role</TableHead>
                <TableHead className="font-cabinet font-semibold text-foreground">Provider</TableHead>
                <TableHead className="font-cabinet font-semibold text-foreground">Joined</TableHead>
                <TableHead className="font-cabinet font-semibold text-foreground">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user: User) => (
                <TableRow key={user._id} className="hover:bg-secondary/50 border-b border-border transition-colors">
                  <TableCell className="font-medium font-ibm">
                    <div className="truncate max-w-[150px]" title={user.name || ""}>
                      {user.name || "N/A"}
                    </div>
                  </TableCell>
                  <TableCell className="font-ibm text-muted-foreground">
                    <div className="truncate max-w-[200px]" title={user.email}>
                      {user.email}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.role === "admin" ? "default" : "secondary"} className={`font-ibm ${user.role === 'admin' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                      {user.role || "user"}
                    </Badge>
                  </TableCell>
                  <TableCell className="capitalize font-ibm">{user.auth_provider}</TableCell>
                  <TableCell className="font-ibm text-sm">
                    {new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.is_active ? "outline" : "destructive"} className={`font-ibm ${user.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-destructive/10 text-destructive border-destructive/20"}`}>
                      {user.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground font-ibm">
                    No users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination Controls - Fixed at Bottom */}
      <div className="flex-none pt-4">
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium text-muted-foreground">Rows per page</p>
            <Select
              value={`${limit}`}
              onValueChange={(value) => {
                updateParams(1, Number(value), debouncedSearch);
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={limit} />
              </SelectTrigger>
              <SelectContent side="top">
                {[10, 20, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="flex w-[100px] items-center justify-center text-sm font-medium text-muted-foreground">
              Page {page} of {totalPages || 1}
            </div>
            <Button
              variant="outline"
              className="h-8 w-8 p-0 bg-card hover:bg-secondary border-border"
              onClick={() => updateParams(Math.max(1, page - 1), limit, debouncedSearch)}
              disabled={page === 1}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0 bg-card hover:bg-secondary border-border"
              onClick={() => updateParams(Math.min(totalPages, page + 1), limit, debouncedSearch)}
              disabled={page === totalPages || totalPages === 0}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}